import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import {
  getAccessToken,
  getUserId,
  getOrganizationId,
} from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";


export const createFund = createAsyncThunk(
  "funds/createFund",
  async (fundData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const donorId = getUserId();
      const organizationId = getOrganizationId();

      // Transform UI data to match API structure
      const transformedData = {
        title: fundData.transactionName,
        description: fundData.description,
        amount: parseFloat(fundData.amount),
        transactionChannel: {
          debitingAccountID: fundData.debitAccount, // Account ID from dropdown
          modeOfPaymentID: fundData.paymentMode, // 'transfer' or 'card'
        },
        organizationID: organizationId,
        campaignID: organizationId,
        donorID: donorId,
      };

      const response = await secureAxios.post(
        `${BASE_URL}/v1/CampaignFunds`,
        transformedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchFunds = createAsyncThunk(
  "funds/fetchFunds",
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(`${BASE_URL}/v1/CampaignFunds`, {
        params: {
          skip,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return {
        funds: response.data.data,
        total: response.data.total || response.data.data.length,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchFundByAny = createAsyncThunk(
  "funds/fetchFundByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/CampaignFunds/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return {
        data: response.data.data,
        total: response.data.data.length, // Include the total count
        isSingleFund: key === "_id", // Flag to indicate if we're fetching by ID
      };
    } catch (error) {
      // If it's a 404 error (not found), return an empty array instead of rejecting
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleFund: false };
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateFund = createAsyncThunk(
  "funds/updateFund",
  async (fundData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/CampaignFunds`,
        fundData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteFund = createAsyncThunk(
  "funds/deleteFund",
  async (fundId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(
        `${BASE_URL}/v1/CampaignFunds`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: [fundId],
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  funds: [],
  fetchedFunds: [],
  currentFund: null,
  total: 0,
  fundDetails: {
    transactionName: "",
    description: "",
    amount: "",
    debitAccount: "",
    creditAccount: "",
  },
  isLoading: false,
  error: null,
  success: false,
};

const fundsSlice = createSlice({
  name: "funds",
  initialState,
  reducers: {
    updateFundDetails: (state, action) => {
      state.fundDetails = {
        ...state.fundDetails,
        ...action.payload,
      };
    },
    resetFundState: () => initialState,
    loadCachedData: (state, action) => {
      return {
        ...state,
        funds: action.payload.funds || state.funds,
        fetchedFunds: action.payload.fetchedFunds || state.fetchedFunds,
        currentFund: action.payload.currentFund || state.currentFund,
        total: action.payload.total || state.total,
        fundDetails: action.payload.fundDetails || state.fundDetails,
        isLoading: false,
        error: null,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createFund.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createFund.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createFund.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFunds.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFunds.fulfilled, (state, action) => {
        state.isLoading = false;
        state.funds = action.payload.funds;
        state.total = action.payload.total;
        state.error = null;
      })
      .addCase(fetchFunds.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFundByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFundByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedFunds = action.payload.data;

        if (action.payload.isSingleFund && action.payload.data.length > 0) {
          state.currentFund = action.payload.data[0];
        } else if (action.payload.data.length > 0) {
          state.currentFund = action.payload.data[0];
        }

        // Update total if we're fetching a list
        if (!action.payload.isSingleFund) {
          state.total = action.payload.total;
        }

        state.error = null;
      })
      .addCase(fetchFundByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateFund.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateFund.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateFund.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteFund.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteFund.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteFund.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const { updateFundDetails, resetFundState, loadCachedData } =
  fundsSlice.actions;

// Export selectors
export const selectFundDetails = (state) => state.funds.fundDetails;
export const selectFundLoading = (state) => state.funds.isLoading;
export const selectFundError = (state) => state.funds.error;
export const selectFundSuccess = (state) => state.funds.success;
export const selectAllFunds = (state) => state.funds.funds;
export const selectFetchedFunds = (state) => state.funds.fetchedFunds || [];
export const selectCurrentFund = (state) => state.funds.currentFund;
export const selectFundTotal = (state) => state.funds.total;

export const selectIsOfflineData = (state) =>
  state.funds.isOfflineData || false;
export default fundsSlice.reducer;
